import { u } from "./url";
import { csrfToken } from "./csrf";
import { showToast, type ToastType } from "./toast";

/** 知らせは下のトーストで (旧実装は alert で画面を止めていた / 成否を何も言わなかった) */
function say(msg: string, type: ToastType): void {
  showToast(msg, type);
}
/**
 * Push subscription UI helper.
 *  - /push/vapid から公開鍵を取得
 *  - SW を登録
 *  - PushManager.subscribe → /push/subscribe へ送る
 *  - 解除ボタンで /push/unsubscribe
 *
 * UI ボタンは「通知を有効にする / 無効にする」をトグル。
 */

interface VAPIDResp {
  publicKey: string;
}

function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(u("/sw.js"), { scope: u("/") });
  } catch (e) {
    console.warn("[push] SW register failed", e);
    return null;
  }
}

async function fetchVapid(): Promise<string> {
  const res = await fetch(u("/push/vapid"));
  if (!res.ok) throw new Error("vapid fetch failed");
  const j = (await res.json()) as VAPIDResp;
  return j.publicKey;
}

async function subscribe(): Promise<boolean> {
  if (!("PushManager" in window)) {
    say("このブラウザは通知に対応していません", "warn");
    return false;
  }
  const reg = await registerSW();
  if (!reg) return false;

  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    say("通知を許可すると受け取れます (ブラウザの設定から)", "warn");
    return false;
  }

  const publicKey = await fetchVapid();
  // TS 5.7+ の Uint8Array<ArrayBufferLike> と DOM の BufferSource (ArrayBuffer 限定) で
  // 型衝突するため明示的に ArrayBuffer 由来であることをキャストで伝える。
  const keyBytes = urlBase64ToUint8Array(publicKey);
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes.buffer as ArrayBuffer,
  });

  const res = await fetch(u("/push/subscribe"), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(sub.toJSON()),
  });
  if (!res.ok) {
    console.warn("[push] subscribe POST failed", res.status);
    return false;
  }
  return true;
}

/**
 * ensureCurrentAccountSubscribed は「すでに通知を許可済み」の端末で、ページ読み込み
 * ごとに現在ログイン中アカウントの push 購読を静かに作り直す/再送する。
 * プロンプトは一切出さない (Notification.permission === "granted" の時だけ動く)。
 *
 * これが無いと多重アカウント端末で次の事故が起きる:
 *  - 別アカウントへ切替えても新アカウントの購読行が無く is_current_account が立たず、
 *    DM 以外の push (新規投稿/返信/いいね 等) が全部消える。
 *  - 一度許可しても、別アカウントの subscribe で自分の行が is_current=0 に降格される。
 * 結果が「アプリ内ベルは出るが OS 通知/バッジ/バイブが来ない」= 今回の不具合。
 *
 * 再 POST により Repo.Save が (現アカウント, endpoint) を is_current=1 に upsert し、
 * 同 endpoint の他アカウント行を 0 に降格する → 常にアクティブなアカウントへ届く。
 */
export async function ensureCurrentAccountSubscribed(): Promise<void> {
  try {
    if (!(window as unknown as { V2_CURRENT_USER_ID?: number }).V2_CURRENT_USER_ID) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const reg = await registerSW();
    if (!reg) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // 許可済みなのに購読が無い (新アカウント / 再インストール後) → プロンプト無しで購読を作る。
      const publicKey = await fetchVapid();
      const keyBytes = urlBase64ToUint8Array(publicKey);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes.buffer as ArrayBuffer,
      });
    }
    // 既存購読でも再 POST して現在のアカウントを is_current_account=1 に昇格させる。
    await fetch(u("/push/subscribe"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify(sub.toJSON()),
    });
  } catch (e) {
    /* fail-quiet: push が無くてもアプリ本体は動く */
  }
}

async function unsubscribe(): Promise<boolean> {
  const reg = await navigator.serviceWorker.getRegistration(u("/"));
  if (!reg) return true;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  await fetch(u("/push/unsubscribe"), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });
  await sub.unsubscribe();
  return true;
}

async function currentState(): Promise<"on" | "off" | "unsupported"> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  const reg = await navigator.serviceWorker.getRegistration(u("/"));
  if (!reg) return "off";
  const sub = await reg.pushManager.getSubscription();
  return sub ? "on" : "off";
}

// ベルの線画 (templates/partials/line_icons.html の ic_bell と同じ path)。
// 2026-09-23 オーナー指示「絵文字 (🔔 / 🔕) をやめて独自の CSS に」— 絵文字は端末ごとに絵が違い、
// ライトでは黄色い塊になっていた。OFF は同じベルに斜線 1 本 (fill:none / stroke:currentColor は CSS の .dic)。
const BELL_ON = '<svg class="dic pb-bell" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 17.2H4c1.35-1.45 2.2-3.35 2.2-5.4V9.8a5.8 5.8 0 0 1 11.6 0v2c0 2.05.85 3.95 2.2 5.4z"/><path d="M9.9 19.6a2.2 2.2 0 0 0 4.2 0"/></svg>';
const BELL_OFF = '<svg class="dic pb-bell pb-bell--off" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 17.2H4c1.35-1.45 2.2-3.35 2.2-5.4V9.8a5.8 5.8 0 0 1 11.6 0v2c0 2.05.85 3.95 2.2 5.4z"/><path d="M9.9 19.6a2.2 2.2 0 0 0 4.2 0"/><path d="M4.5 4.5l15 15"/></svg>';

function setLabel(btn: HTMLButtonElement, state: "on" | "off" | "unsupported"): void {
  if (state === "unsupported") {
    btn.textContent = "通知 (非対応)";
    btn.disabled = true;
    return;
  }
  btn.classList.toggle("is-on", state === "on");
  // 文字は「いまの状態」を言う (X と同じ)。押すと反対になる。
  btn.innerHTML = state === "on" ? BELL_ON + "<span>通知 ON</span>" : BELL_OFF + "<span>通知を有効にする</span>";
  btn.title = state === "on" ? "押すと通知を解除します" : "押すとこの端末に通知が届くようになります";
}

export function initPushButton(): void {
  const btn = document.querySelector<HTMLButtonElement>(".v2-push-toggle");
  if (!btn) return;
  currentState().then((s) => setLabel(btn, s));
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    const s = await currentState();
    if (s === "unsupported") return;
    try {
      if (s === "on") {
        await unsubscribe();
      } else {
        await subscribe();
      }
      const st = await currentState();
      setLabel(btn, st);
      if (st === "on") say("通知をオンにしました", "success");
      else if (s === "on") say("通知をオフにしました", "success");
    } catch (e) {
      console.warn("[push] toggle failed", e);
      say("通知の設定を変えられませんでした", "error");
    } finally {
      btn.disabled = false;
    }
  });

  // ログイン直後の自動登録は強制せず、テスト用ボタンも提供する
  const testBtn = document.querySelector<HTMLButtonElement>(".v2-push-test");
  testBtn?.addEventListener("click", async () => {
    try {
      const r = await fetch(u("/push/test"), {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken() },
      });
      say(r.ok ? "テスト通知を送りました" : "テスト通知を送れませんでした", r.ok ? "success" : "error");
    } catch {
      say("テスト通知を送れませんでした", "error");
    }
  });
}
